// src/pages/AttendanceOverview/components/TimeSheetManagementTab.js
import React, { useEffect, useState } from "react";
import { Card, Table, DatePicker, Col, Row } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { fetchTimeSheet, fetchAllEmployees } from "../attendanceOverviewThunk";
import {
  selectTimeSheetReport,
  selectEmployees,
} from "../attendanceOverviewSelectors";
import ActionOptionsForTimeSheet from "./ActionOptionsForTimeSheet";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import { accessChecker } from "../../../lib/functions";

const TimeSheetManagementTab = () => {
  const dispatch = useDispatch();
  const timeSheetReport = useSelector(selectTimeSheetReport) || [];
  const employees = useSelector(selectEmployees) || [];
  const [filters, setFilters] = useState({
    month: null,
    year: null,
    employee_id: null,
    status: null,
  });
  const [pagination, setPagination] = useState({current: 1, pageSize: 10});

  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTimeSheet(filters));
  }, [dispatch, filters]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const status = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const columns = [
    { title: "No.", render: (_, __, i) => i + 1,},
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (text) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 160,
          }}
        >
          {text}
        </div>
      ),
    },
    { title: "Client Name", dataIndex: "client_name", key: "client_name" },
    {
      title: "Approver Name",
      dataIndex: "client_user_name",
      key: "client_user_name",
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      render: (value) => monthNames[value - 1] || "Invalid Month",
    },
    {
      title: "Link Status",
      dataIndex: "is_used",
      key: "is_used",
      render: (value) => (value === 1 ? "Used" : "Not Used"),
    },
    {
      title: "Created At",
      dataIndex: "created_date",
      key: "created_date",
      render: (value) => {
        if (!value) return "";
        const date = new Date(value);
        const day = date.getDate().toString().padStart(2, "0");
        const month = date.toLocaleString("default", { month: "short" }); // e.g. "Oct"
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
      },
    },
    { title: "Status", dataIndex: "status", key: "status" },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
    },
    {
      title: "Resend",
      key: "resend",
      align: "right",
      render: (_, record, index) => (
        <ActionOptionsForTimeSheet
          value={record.id}
          rowIndex={index}
          rowData={record}
          onFilters={filters}
        />
      ),
    },
  ];

  const onTimeSheetMonthChange = (date) => {
    if (date) {
      const month = date.month() + 1; // dayjs months are 0-based
      const year = date.year();

      setFilters((prev) => ({
        ...prev,
        month,
        year,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        month: null,
        year: null,
      }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };
  return (
    <Card className="page-filters">
      <div
        style={{ marginBottom: 12 }}
        className="page-filters mb-4 shadow-md rounded-xl"
      >
        <Row
          gutter={[16, 16]}
          align="middle"
          wrap
          style={{ alignItems: "center" }}
        >
          {accessChecker("approve_attendance") && (
            <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
              <label>All Users</label>
              <SearchListAutoComplete
                optionsList={employees.map((c) => ({
                  sale_id: c.id,
                  display_text: c.user_name,
                }))}
                onSelect={(obj) =>
                  handleFilterChange("employee_id", obj?.sale_id)
                }
                placeholder="Account"
                showAddNew={false}
                allowClear
              />
            </Col>
          )}

          <Col
            xs={24}
            sm={12}
            md={4}
            lg={6}
            xl={5}
            xxl={4}
            style={{ paddingBottom: "5px" }}
          >
            <label>Select Month</label>
            <DatePicker
              picker="month"
              onChange={onTimeSheetMonthChange}
              placeholder="Select Month"
              style={{ width: "100%", padding: "0px 11px" }}
            />
          </Col>
          <Col xs={24} sm={12} md={4} lg={6} xl={4} xxl={4}>
            <label>Status</label>
            <SearchListAutoComplete
              optionsList={status.map((c) => ({
                sale_id: c.value,
                display_text: c.label,
              }))}
              onSelect={(obj) => handleFilterChange("status", obj?.sale_id)}
              placeholder="Account"
              showAddNew={false}
              allowClear
            />
          </Col>
        </Row>
        <style>{`
    @media (min-width: 768px) and (max-width: 1168px) {
      .page-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
        @media (min-width: 768px) {
  :where(.css-dev-only-do-not-override-1j5i8gz).ant-col-md-4 {
    max-width: none !important;
    flex: none !important;
  }
  `}</style>
      </div>

      <Table
        columns={columns}
        dataSource={timeSheetReport}
        rowKey={(r) => r.id || Math.random()}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,

          showSizeChanger: true,
          showQuickJumper: true,

          pageSizeOptions: ["10", "20", "50", "100"],

          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,

          onChange: (page, pageSize) => {
            setPagination({
              current: page,
              pageSize,
            });
          },

          onShowSizeChange: (current, size) => {
            setPagination({
              current: 1,
              pageSize: size,
            });
          },
        }}
        bordered
        size={"middle"}
        scroll={{ x: "max-content" }}
      />
    </Card>
  );
};

export default TimeSheetManagementTab;
